// src/components/TopNav.js

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import logo from '../assets/logo-768x114.png';
import {
  IoTimerOutline,
  IoMailOutline,
  IoSettingsOutline,
  IoBarbellOutline,
  IoLogOutOutline,
  IoInformationCircleOutline,
  IoBookOutline,
  IoTrophyOutline,
  IoNotificationsOutline,
  IoPeopleOutline,
  IoChevronDownOutline,
  IoSparklesOutline,
} from 'react-icons/io5';
import { useAppContext } from '../context/AppContext';
import api from '../api';

const TopNav = () => {
  const { user, logout } = useAuthContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { openTimer } = useAppContext();
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);

  const displayName = user?.name || user?.email?.split('@')[0] || 'Usuario';
  const firstLetter = displayName.charAt(0).toUpperCase();
  const isStaff = ['coach', 'admin'].includes(user?.role);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!user || isStaff) return;
    const fetchUnread = async () => {
      try {
        const res = await api.get('/me/messages/unread-count');
        setUnreadCount(res.data.unread || 0);
      } catch {
        // silently ignore
      }
    };
    fetchUnread();
    pollRef.current = setInterval(fetchUnread, 60000);
    return () => clearInterval(pollRef.current);
  }, [user, isStaff]);

  return (
    <nav className="relative w-full bg-black/30 backdrop-blur-xl border-b border-white/10 shadow-lg p-2 z-40">
      <div className="container mx-auto flex justify-between items-center h-12 md:h-14">
        
        {/* Left: greeting on desktop */}
        <div className="w-1/3 hidden md:flex items-center">
          {user && (
            <span className="text-gray-400 text-sm">
              Hola, <span className="text-white font-semibold">{displayName.split(' ')[0]}</span>
              {isStaff && (
                <span className="ml-2 text-xs bg-marizan-red/20 text-marizan-red border border-marizan-red/30 rounded-full px-2 py-0.5">
                  {isAdmin ? 'Admin' : 'Coach'}
                </span>
              )}
            </span>
          )}
        </div>
        {/* Mobile: empty left spacer */}
        <div className="w-1/3 md:hidden" />

        {/* Center: logo */}
        <div className="w-1/3 flex justify-center">
          <Link to="/dashboard">
            <img src={logo} alt="Marizan Fitness Logo" className="h-10 md:h-12 drop-shadow-[0_0_12px_rgba(255,255,255,0.2)]" />
          </Link>
        </div>

        {/* Right: timer + avatar */}
        <div className="w-1/3 flex justify-end items-center space-x-2 md:space-x-4 text-white">
          <button onClick={openTimer} className="hover:text-marizan-red transition-colors p-1.5 rounded-full hover:bg-white/10" title="Temporizador">
            <IoTimerOutline size={26} />
          </button>

          {!isStaff && (
            <Link
              to="/messages"
              className="relative hover:text-marizan-red transition-colors p-1.5 rounded-full hover:bg-white/10"
              title="Mensajes del Coach"
            >
              <IoNotificationsOutline size={26} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-marizan-red text-white text-[10px] font-bold px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          )}

          <div className="relative">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="flex items-center gap-2 hover:bg-white/10 rounded-full pl-1 pr-2 py-1 transition-colors"
            >
              {/* Avatar circle with initial */}
              <div className="w-8 h-8 rounded-full bg-marizan-red/80 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                {firstLetter}
              </div>
              {/* "Menú" label on md+ screens */}
              <span className="hidden md:block text-sm font-medium text-white">Menú</span>
              <IoChevronDownOutline size={14} className="hidden md:block text-gray-400" />
            </button>

            {isMenuOpen && (
              <div 
                className="absolute right-0 mt-2 w-64 glass-panel rounded-xl py-2 shadow-2xl"
                onMouseLeave={() => setIsMenuOpen(false)}
              >
                {user && (
                  <div className="px-4 py-3 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-marizan-red/80 flex items-center justify-center text-base font-bold text-white flex-shrink-0">
                        {firstLetter}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-bold truncate">{displayName}</p>
                        <p className="text-xs text-gray-400 truncate">{user.email}</p>
                        {isStaff && (
                          <span className="text-xs bg-marizan-red/20 text-marizan-red border border-marizan-red/30 rounded-full px-2 py-0.5 mt-1 inline-block">
                            {isAdmin ? 'Admin' : 'Coach'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {!isAdmin && (
                  <Link to="/messages" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                    <IoMailOutline className="mr-3 flex-shrink-0" size={18} /> Mensajes del Coach
                  </Link>
                )}
                {!isAdmin && isStaff && (
                  <Link to="/coach/reminders" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                    <IoMailOutline className="mr-3 flex-shrink-0" size={18} /> Recordatorios
                  </Link>
                )}
                {isStaff && (
                  <Link to="/coach/quotes" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                    <IoSparklesOutline className="mr-3 flex-shrink-0" size={18} /> Frases Motivacionales
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin/users" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                    <IoPeopleOutline className="mr-3 flex-shrink-0" size={18} /> Gestionar Usuarios
                  </Link>
                )}
                <Link to="/exercises" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  <IoBarbellOutline className="mr-3 flex-shrink-0" size={18} /> Lista de Ejercicios
                </Link>
                <Link to="/profile-settings" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  <IoSettingsOutline className="mr-3 flex-shrink-0" size={18} /> Preferencias
                </Link>
                {!isAdmin && (
                  <Link to={isStaff ? '/achievements/manage' : '/achievements'} onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                    <IoTrophyOutline className="mr-3 flex-shrink-0" size={18} /> Logros
                  </Link>
                )}
                <Link to="/manual" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  <IoBookOutline className="mr-3 flex-shrink-0" size={18} /> Manual
                </Link>
                <Link to="/about" onClick={() => setIsMenuOpen(false)} className="flex items-center w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  <IoInformationCircleOutline className="mr-3 flex-shrink-0" size={18} /> Info
                </Link>
                <div className="border-t border-white/10 mt-1 pt-1">
                  <button
                    onClick={() => { setIsMenuOpen(false); logout(); }}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                  >
                    <IoLogOutOutline className="mr-3 flex-shrink-0" size={18} /> Cerrar sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNav;
